import { memo, useCallback, useMemo } from "react";

import { PlainWidgetDecoration, type PlainWidgetItemInput } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";
import type * as FormModelType from "@/components/features/design/plain/model/type";

import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";

import { Accordion } from "../accordion";
import { FormIslandSection } from "../island-section";
import { AddSnackWithDialog } from "./add-snack-with-dialog";
import { PlainWidgetDecorationCard } from "./plain-widget-decoration-card";

const SampleSnackUrl = "/assets/design/plain/sample-snack.jpg";

type Props = {
	hotelID: string;
	// snack
	primaryColor: ForNodeType<string>;
	widgetDecoration: PlainWidgetDecoration;
	snacks: FormModelType.QueryData["snackList"]["snacks"];
	plainWidgetItems: Array<PlainWidgetItemInput>;

	onAddPlainWidgetDefault: (contentID: string, title: string) => void;
	onRemovePlainWidgetDefault: (widgetID: string) => void;
	onUpdateOrderPlainWidgets: (widgetIDs: Array<string>) => void;
	onChangeWidgetDecoration: (value: PlainWidgetDecoration) => void;

	// magazine
	isShowMagazineAbout: boolean;
	isShowMagazineRoom: boolean;
	isShowMagazinePlan: boolean;

	onChangeIsShowMagazineAbout: (value: boolean) => void;
	onChangeIsShowMagazineRoom: (value: boolean) => void;
	onChangeIsShowMagazinePlan: (value: boolean) => void;
};

export const ContentsSettingForm = memo(
	({
		hotelID,
		// snack
		primaryColor,
		widgetDecoration,
		snacks,
		plainWidgetItems,

		onChangeWidgetDecoration,
		onAddPlainWidgetDefault,
		onRemovePlainWidgetDefault,
		onUpdateOrderPlainWidgets,
		// magazine
		isShowMagazineAbout,
		isShowMagazinePlan,
		isShowMagazineRoom,

		onChangeIsShowMagazineAbout,
		onChangeIsShowMagazinePlan,
		onChangeIsShowMagazineRoom,
	}: Props) => {
		const handleChangeIsShowMagazineAbout = useCallback(
			(value: boolean) => {
				onChangeIsShowMagazineAbout(value);
			},
			[onChangeIsShowMagazineAbout],
		);

		const handleChangeIsShowMagazinePlan = useCallback(
			(value: boolean) => {
				onChangeIsShowMagazinePlan(value);
			},
			[onChangeIsShowMagazinePlan],
		);

		const handleChangeIsShowMagazineRoom = useCallback(
			(value: boolean) => {
				onChangeIsShowMagazineRoom(value);
			},
			[onChangeIsShowMagazineRoom],
		);

		const thumbnail = useMemo(() => {
			return snacks.at(0)?.thumbnail ?? SampleSnackUrl;
		}, [snacks]);

		return (
			<FormIslandSection title="コンテンツ設定">
				<Accordion labelChildren={<label className="text-[#4c4c4c]">スナックの設定</label>}>
					<div className="relative">
						<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs laptop:hidden">
							スナックの設定はPC版からのみ変更できます
						</div>
						<div
							className="pointer-events-none divide-y divide-zinc-200 opacity-40 laptop:pointer-events-auto laptop:opacity-100"
							aria-disabled="true"
						>
							<AddSnackWithDialog
								hotelID={hotelID}
								snacks={snacks}
								plainWidgetItems={plainWidgetItems}
								onAddPlainWidget={onAddPlainWidgetDefault}
								onRemovePlainWidget={onRemovePlainWidgetDefault}
								onUpdateOrderPlainWidgets={onUpdateOrderPlainWidgets}
							/>

							<div className="space-y-3 px-2 py-3">
								<label>枠の設定</label>

								<div className="flex flex-wrap gap-4">
									<PlainWidgetDecorationCard
										thumbnail={thumbnail}
										primaryColor={primaryColor.value}
										title="なし"
										target={PlainWidgetDecoration.None}
										current={widgetDecoration}
										onClickCard={onChangeWidgetDecoration}
									/>

									<PlainWidgetDecorationCard
										thumbnail={thumbnail}
										primaryColor={primaryColor.value}
										title="塗り"
										target={PlainWidgetDecoration.Fill}
										current={widgetDecoration}
										onClickCard={onChangeWidgetDecoration}
									/>

									<PlainWidgetDecorationCard
										thumbnail={thumbnail}
										primaryColor={primaryColor.value}
										title="線"
										target={PlainWidgetDecoration.Line}
										current={widgetDecoration}
										onClickCard={onChangeWidgetDecoration}
									/>
								</div>
							</div>
						</div>
					</div>
				</Accordion>
				<Accordion labelChildren={<label className="text-[#4c4c4c]">マガジンの設定</label>}>
					<div className="divide-y divide-zinc-200">
						<div className="px-2 py-3">
							<CheckboxField
								value={isShowMagazineAbout}
								label="施設の記事を表示する"
								handleChange={handleChangeIsShowMagazineAbout}
							/>
						</div>
						<div className="px-2 py-3">
							<CheckboxField
								value={isShowMagazinePlan}
								label="プランの記事一覧を表示する"
								handleChange={handleChangeIsShowMagazinePlan}
							/>
						</div>
						<div className="px-2 py-3">
							<CheckboxField
								value={isShowMagazineRoom}
								label="部屋の記事一覧を表示する"
								handleChange={handleChangeIsShowMagazineRoom}
							/>
						</div>
					</div>
				</Accordion>
			</FormIslandSection>
		);
	},
);
